import {
  Button,
  Callout,
  Collapsible,
  Skeleton,
  Tooltip,
  toast,
} from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { useIsMutating } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ListChecks, Trash2 } from "lucide-react";
import { useState } from "react";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { useDeleteRecommendations } from "../network/useDeleteRecommendations";
import { useRecommendations } from "../network/useRecommendations";
import { AddRecommendation } from "./AddRecommendation";
import { Recommendation } from "./Recommendation";

interface RecommendationsProps {
  carePlanId: string;
  encounterId: string;
  totalRecommendations: number;
}

export const Recommendations = ({
  carePlanId,
  encounterId,
  totalRecommendations,
}: RecommendationsProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useRecommendations({
    encounterId,
    carePlanId,
    enabled: open,
  });

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const { mutateAsync: deleteAllRecommendations, isPending: isDeletingAll } =
    useDeleteRecommendations({ encounterId, carePlanId });

  // True while ANY single or bulk recommendation void is in flight for this
  // careplan. Used to disable "Remove All" while a per-row single void is
  // pending so the two can't race into a FHIR 409 conflict (EHR-8188).
  const isAnyVoidPending =
    useIsMutating({
      mutationKey:
        QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.Recommendation.VOID({
          patientId,
          encounterId,
          carePlanId,
        }),
    }) > 0;

  const canReadRecommendations = isActionAvailable(
    CareplanAvailableAction.READ_RECOMMENDATION,
    careplanAvailableActions,
  );

  const canAddRecommendations = isActionAvailable(
    CareplanAvailableAction.ADD_RECOMMENDATION,
    careplanAvailableActions,
  );

  const handleDeleteAllRecommendations = async () => {
    try {
      await deleteAllRecommendations();
    } catch (error) {
      toast.error("Failed to delete all recommendations.");
    }
  };

  if (!canReadRecommendations) {
    return null;
  }

  if (isError) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load recommendations."
      />
    );
  }

  const removeAllAction =
    totalRecommendations && canAddRecommendations ? (
      <Tooltip
        tooltipContentText="Remove All"
        side="bottom"
        triggerClassName="flex items-center"
      >
        <Button
          variant="icon"
          className="m-0 p-0"
          onClick={handleDeleteAllRecommendations}
          aria-label="Remove all recommendations"
          isLoading={isDeletingAll}
          disabled={isAnyVoidPending}
        >
          <Trash2 className="h-4 w-4 stroke-error" />
        </Button>
      </Tooltip>
    ) : null;

  return (
    <Collapsible
      title="Recommendations"
      headerSize="sm"
      onOpenChange={(open) => {
        trackEvent(
          open
            ? TRACKERS.EXPAND_RECOMMENDATIONS
            : TRACKERS.COLLAPSE_RECOMMENDATIONS,
        );
        setOpen(open);
      }}
      titleIcon={<ListChecks className="w-4 h-4" />}
      status={
        totalRecommendations ? `${totalRecommendations.toString()}` : "None"
      }
      customActions={removeAllAction ? [removeAllAction] : []}
    >
      <div className="flex flex-col">
        {isFetching ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        ) : (
          data?.recommendationTasks.map((task) => {
            return (
              <Recommendation
                key={task.id}
                task={task}
                carePlanId={carePlanId}
                encounterId={encounterId}
              />
            );
          })
        )}
      </div>
      <AddRecommendation carePlanId={carePlanId} encounterId={encounterId} />
    </Collapsible>
  );
};
